import { DateTime } from 'luxon';
import React, { useCallback, useState } from 'react';

import { Heading } from '@/components/typography/heading';
import { useLocalZonePreference } from '@/hooks/useLocalZonePreference';
import type { ActionLog } from '@/types/logs';

import ActionLogDetailView from '../logDetail/actionLogDetailView';
import styles from './actionLogViewer.module.scss';

type Props = {
  agentId: string;
  requestId?: string;
  correlactionId?: string;
  data: ActionLog[] | Record<string, ActionLog>;
  initialSelectedId?: string;
  onSelectionChange?: ( id: string ) => void;
};


function ActionLogViewer( props: Props ) {
  const [ selectedId, setSelectedId ] = useState<string | null>( props.initialSelectedId ?? null );
  const [ useLocalTimeZone ] = useLocalZonePreference();

  // Convert object to array if needed
  const logs: ActionLog[] = Array.isArray( props.data )
    ? props.data
    : Object.values( props.data || {} );

  // Get selected log details
  const selectedLog = logs.find( ( log ) => log.id === selectedId );

  const formatTime = useCallback( ( timestamp: string | undefined ): string => {
    if ( !timestamp ) return 'N/A';

    // Parse ISO string as UTC
    const dt = DateTime.fromISO( timestamp, { zone: 'utc' } );

    // Convert to UTC or local based on preference
    const zonedDt = useLocalTimeZone ? dt.toLocal() : dt.toUTC();

    // Format: "2/4/2026, 10:51:34 AM (PST)"
    return zonedDt.toFormat( 'M/d/yyyy, h:mm:ss a (ZZZZ)' );
  }, [ useLocalTimeZone ] );

  return (
    <div className={ styles.logViewer }>
      {/* Left Panel - List View */}
      <div className={ styles.sidebar }>
        <div className={ styles.header }>Agent Logs ({logs.length})</div>

        <div className={ styles.list }>
          {logs.map( ( log ) => (
            <div
              key={ log.id }
              onClick={ () => { setSelectedId( log.id ); props.onSelectionChange?.( log.id ); } }
              className={ `${ styles.listItem } ${ selectedId === log.id ? styles.listItemSelected : '' }` }
            >
              <div className={ styles.listItemTime }>
                {formatTime( log.start_time )}
              </div>
              <div className={ styles.listItemAgent }>{log.component_name}</div>
              <div className={ styles.listItemId }>
                ID: {log.id.slice( 0, 8 )}...
              </div>
              <div className={ styles.listItemCorrelation }>
                {log.correlation_id}
              </div>
            </div>
          ) )}

          {logs.length === 0 && (
            <div className={ styles.empty }>No logs to display</div>
          )}
        </div>
      </div>

      {/* Right Panel - Detail View */}
      <div className={ styles.content }>
        {selectedLog ? (
          <div className={ styles.details }>
            <Heading as="h2" size="md" className={ styles.detailsTitle }>Log Details</Heading>
            <ActionLogDetailView selectedLog={ selectedLog }/>
          </div>
        ) : (
          <div className={ styles.placeholder }>Select a log to view details</div>
        )}
      </div>
    </div>
  );
}

export default ActionLogViewer;
